import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ProcessSteps from "./ProcessSteps";

// FAQItem component remains the same, it's already reusable
const FAQItem = ({ question, answer, variant = "default" }) => {
  const [isOpen, setIsOpen] = useState(false);
  useTranslation();
  const isWallarts = variant === "wallarts";

  return (
    <div
      className={
        isWallarts
          ? "mb-4 overflow-hidden rounded-[1.5rem] border border-[rgba(191,174,158,0.32)] bg-white/72 shadow-[0_18px_45px_rgba(122,92,62,0.08)] font-raleway backdrop-blur-sm"
          : "border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm font-raleway"
      }
    >
      <button
        className={
          isWallarts
            ? "flex w-full items-center justify-between bg-white/70 p-5 text-left transition-colors duration-200 hover:bg-white/90 focus:outline-none"
            : "w-full text-left p-5 flex justify-between items-center bg-white hover:bg-gray-50 focus:outline-none transition-colors duration-200"
        }
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span
          className={
            isWallarts
              ? "text-lg font-semibold text-[#7a5c3e]"
              : "text-lg font-semibold text-[#83375B]"
          }
        >
          {question}
        </span>
        <svg
          className={`w-6 h-6 transform transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          fill="none"
          stroke={isWallarts ? "#8f6f58" : "currentColor"}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>
      <div
        className={`px-5 pt-4 transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-screen pb-5" : "max-h-0"
        } ${isWallarts ? "text-[#8d735f]" : "text-gray-700"}`}
      >
        <p>{answer}</p>
      </div>
    </div>
  );
};

// Modified FAQSection to accept 'faqs' and 'title' as props
const FAQSection = ({
  faqs,
  title = "Frequently Asked Questions",
  variant = "default",
}) => {
  const { t } = useTranslation();
  const isWallarts = variant === "wallarts";

  const ITEMS_PER_PAGE = 5; // Number of FAQs to show initially and add on "Show More"
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);

  const handleShowMore = () => {
    setVisibleItems((prevVisibleItems) => prevVisibleItems + ITEMS_PER_PAGE);
  };

  // Determine if there are more items to show
  const hasMoreItems =
    faqs && Array.isArray(faqs) && visibleItems < faqs.length;

  return (
    <>
      <ProcessSteps variant={variant} />
      <section
        className={
          isWallarts
            ? "bg-gradient-to-br from-[#f7f3ef] via-[#e6ded7] to-[#f3e9e0] px-4 py-14 sm:px-6 lg:px-8"
            : "canvas-background lg:pt-4 pt-12 md:py-12 px-4 sm:px-6 lg:px-8 lg:py-5 py-12"
        }
      >
        <div className="max-w-4xl mx-auto">
          {isWallarts && (
            <div className="mb-4 text-center">
              <span className="inline-flex items-center rounded-full border border-[#d7c4b8] bg-white/55 px-4 py-1.5 text-[0.72rem] tracking-[0.26em] uppercase text-[#7a5c3e] backdrop-blur-sm">
                {t("wallartsPage.faqEyebrow", "Helpful Details")}
              </span>
            </div>
          )}
          <h2
            className={
              isWallarts
                ? "mb-8 text-center text-3xl font-semibold tracking-[-0.02em] text-[#8f6f58] md:text-4xl font-raleway"
                : "md:text-3xl text-[1.2rem]  font-bold text-center text-[#83375B] mb-8"
            }
          >
            {title}
          </h2>
          <div>
            {/* Check if faqs prop is provided and is an array before mapping */}
            {faqs &&
              Array.isArray(faqs) &&
              faqs.slice(0, visibleItems).map(
                (
                  faq,
                  index, // Slice the array to show only visible itemscanvas-background lg:pt-4 pt-12 md:py-12 px-4 sm:px-6 lg:px-8 py-5
                ) => (
                  <FAQItem
                    key={index} // It's better to use a unique ID from faq object if available, otherwise index is fallback
                    question={faq.question}
                    answer={faq.answer}
                    variant={variant}
                  />
                ),
              )}
            {/* Optional: Add a message if no FAQs are provided */}
            {(!faqs || faqs.length === 0) && (
              <p className="text-center text-gray-600">
                No FAQs available for this section.
              </p>
            )}
          </div>

          {/* Show More Button */}
          {hasMoreItems && (
            <div className="text-center mt-8">
              <button
                onClick={handleShowMore}
                className={
                  isWallarts
                    ? "rounded-full bg-gradient-to-r from-[#bfae9e] to-[#7a5c3e] px-6 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:scale-105 hover:from-[#7a5c3e] hover:to-[#bfae9e]"
                    : "bg-[#AA7F96] hover:bg-[#916A80] text-white font-bold py-3 px-6 rounded-full transition-colors duration-300 transform hover:scale-105 shadow-md"
                }
              >
                {t("wheatBagPage.wheatBagFaqs.showMore")}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default FAQSection;
